import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom'
import './UserManagement.scss'
import merchantApi from '../../services/merchantApi'
import { Icon } from '@iconify/react/dist/iconify.js'
import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import { ChevronDown, Download, Plus, Search, SlidersHorizontal, X } from 'lucide-react'
import { Divider, Grid, Avatar, Box } from '@mui/material'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import StatusFilterDropdown from '../../components/StatusFilterDropdown/StatusFilterDropdown'
import IconSelect from '../../components/IconSelect/IconSelect';
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal';
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import AppMenu from '../../components/AppMenu/AppMenu';

// ─── Data ─────────────────────────────────────────────────────────────────────

const ROLES_ICON = {
    finance: 'ph:lock-simple-fill',
    support: 'ph:tag-fill',
    ops_manager: 'ph:money-fill',
    super_admin: 'ph:crown-simple-fill',
}

const ROLES = [
    {
        id: 'finance',
        name: 'Finance',
        description: 'Reports and WA credit management.',
        color: 'finance',
        icon: 'ph:money-fill',
    },
    {
        id: 'support',
        name: 'Support',
        description: 'Read-only access to merchant data.',
        color: 'support',
        icon: 'ph:tag-fill',
    },
    {
        id: 'ops_manager',
        name: 'Ops Manager',
        description: 'Full merchant operations.',
        color: 'ops_manager',
        icon: 'ph:gear-six-fill',
    },
    {
        id: 'super_admin',
        name: 'Super Admin',
        description: 'Full platform access. Use with caution.',
        color: 'super_admin',
        icon: 'ph:crown-simple-fill',
    },
]

const ICON_OPTIONS = [
    'ph:lock-simple-fill',
    'ph:tag-fill',
    'ph:money-fill',
    'ph:cooking-pot-fill',
    'ph:fork-knife-fill',
    'ph:users-fill',
    'ph:gear-six-fill',
    'ph:headphones-fill',
    'ph:crown-simple-fill',
]

function normalizePhoneNumber(value) {
    return String(value || '').replace(/^\+?60/, '').replace(/\D/g, '')
}

function getPhoneParts(value) {
    const raw = String(value || '').trim()
    const match = raw.match(/^(\+\d{2,4})/)
    return {
        countryCode: match?.[1] || '60',
        number: raw.replace(/^(\+\d{2,4})/, '').replace(/\D/g, ''),
    }
}

function normalizeAdminPhoneFields(admin = {}) {
    const parsedPhone = parsePhoneNumberFromString(admin.phone ? `+${admin.phone}` : '')
    const fallbackPhone = getPhoneParts(admin.phone)
    console.log('parsedPhone', parsedPhone, admin.phone)
    return {
        ...admin,
        countryCode: parsedPhone?.countryCallingCode ? parsedPhone.countryCallingCode : '60',
        phone: parsedPhone?.nationalNumber || fallbackPhone.number,
    }
}

function normalizeAdmin(admin = {}) {
    const role = admin.roles?.[0] || {}

    return normalizeAdminPhoneFields({
        ...admin,
        role_id: role.id,
        role_name: role.name,
        role_display_name: role.display_name || '-',
    })
}

// ─── Role badge ───────────────────────────────────────────────────────────────

function RoleBadge({ roleId, label }) {
    console.log('roleId', roleId, label)
    const role = ROLES.find((r) => r.name === label)
    const roleLabel = label || role?.name || roleId || '-'

    return (
        <span className={`ar-role-badge ar-role-badge--${role?.color || 'manager'}`}>
            {roleLabel}
        </span>
    )
}

// ─── Invite Admin Modal ───────────────────────────────────────────────────────

function InviteModal({ roles = [], onClose, onInvited }) {
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [phoneCountryCode, setPhoneCountryCode] = useState('60')
    const [email, setEmail] = useState('')
    const [roleId, setRoleId] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [fieldError, setFieldError] = useState({})

    useEffect(() => {
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !submitting) onClose()
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose, submitting])

    const handleSubmit = async () => {
        // if (!name.trim() || !phone.trim() || !roleId || submitting) return

        setSubmitting(true)
        setError('')
        try {
            const payload = {
                name: name.trim(),
                phone: phone ? phoneCountryCode + normalizePhoneNumber(phone): null,
                email: email.trim() || null,
                role_id: Number(roleId),
            }

            const response = await merchantApi.adminUsers.invite(payload)
            onInvited?.(response, payload)
            onClose()
        } catch (err) {
            console.log("Error inviting admin", err, err?.message, err?.errors)
            setError(err?.message || 'Unable to send admin invitation.')
            setFieldError(err?.data?.errors || {})
        } finally {
            setSubmitting(false)
        }
    }
    
    return createPortal(
        <div className="ar-panel-overlay" onClick={onClose}>
            <aside
                className="ar-panel ar-panel--invite"
                role="dialog"
                aria-modal="true"
                aria-labelledby="invite-admin-title"
                onClick={(e) => e.stopPropagation()}
            >

                <div className="ar-panel__header">
                    <h3 id="invite-admin-title">Invite admin user</h3>
                    <button onClick={onClose} aria-label="Close invite admin drawer"><X size={18} /></button>
                </div>

                <div className="ar-panel__body">
                    {error && <div className="ar-error">{error}</div>}

                    <p className="ar-modal__section-label">BASIC INFO</p>

                    <div className="ar-modal__field">
                        <label>Name</label>
                        <input className="ar-input" type="text" placeholder="Amber Ng Zi Qing" value={name} onChange={(e) => setName(e.target.value)} />
                        {fieldError?.name && <div className="ar-error-message">{fieldError.name}</div>}
                    </div>

                    <div className="ar-modal__field">
                        <label>Phone number</label>
                        <PhoneInputField
                            value={phone}
                            onChange={setPhone}
                            countryCode={phoneCountryCode}
                            onCountryCodeChange={setPhoneCountryCode}
                            placeholder="123889021"
                        />
                        {fieldError?.phone && <div className="ar-error-message">{fieldError.phone}</div>}
                    </div>

                    <div className="ar-modal__field">
                        <label>Email address <span className="ar-modal__optional">(optional)</span></label>
                        <input className="ar-input" type="email" placeholder="amberng@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                        {fieldError?.email && <div className="ar-error-message">{fieldError.email}</div>}
                    </div>

                    <p className="ar-modal__section-label">ROLE</p>

                    <div className="ar-role-list">
                        {fieldError?.role_id && <div className="ar-error-message">{fieldError.role_id}</div>}
                        {roles.filter((r) => (r.id !== 'owner' && r.display_name !== 'Owner')).map((role) => {
                            return (
                                <button
                                    key={role.id}
                                    type="button"
                                    className={`ar-role-option ${roleId === role.id ? 'ar-role-option--active' : ''}`}
                                    onClick={() => setRoleId(role.id)}
                                >
                                    <span className={`ar-role-option__icon`}>
                                        <Icon icon={role.name.toLowerCase() in ROLES_ICON ? ROLES_ICON[role.name.toLowerCase()] : role.icon || 'ph:users-fill'} size={18} />
                                    </span>
                                    <span className="ar-role-option__body">
                                        <strong>{role.display_name}</strong>
                                        <span>{role.description}</span>
                                    </span>
                                    {roleId === role.id && (
                                        <span className="ar-role-option__check">✓</span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>

                <div className="ar-panel__footer">
                    <button className="ar-btn ar-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="ar-btn ar-btn--primary" onClick={handleSubmit}>
                        {submitting ? 'Sending...' : 'Send invite'}
                    </button>
                </div>
            </aside>
        </div>,
        document.body
    )
}

// ─── Deactivate Modal ───────────────────────────────────────────────────────

function DeactivateModal({ selectedAdmin, onClose, onDeactivated }) {
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const handleSubmit = async () => {
        if (!selectedAdmin || submitting) return

        setSubmitting(true)
        setError('')

        try {
            const response = await merchantApi.adminUsers.deactivate(selectedAdmin.id)
            onDeactivated(response, selectedAdmin)
            onClose()
        } catch (err) {
            setError(err?.message || 'Unable to deactivate admin account.')
        } finally {
            setSubmitting(false)
        }
    }

    return createPortal(
        <div className="ar-modal-overlay" onClick={onClose}>
            <div className="ar-modal" onClick={(e) => e.stopPropagation()}>

                <div className="ar-modal__header">
                    <h3>Deactivate account</h3>
                    <button className="ar-modal__close" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="ar-modal__body">
                    {error && <div className="ar-error">{error}</div>}

                    <div >Deactivating <strong>{selectedAdmin?.name}</strong> will immediately:</div>

                    <ul className="ar-modal__consequences">
                        <li>Block all login attempts for this account</li>
                        <li>End all active sessions across all devices</li>
                        <li>Preserve audit logs and activity history</li>
                    </ul>

                    <div className='notification-container'>
                        <Icon icon={'ph:info'} className='notification-container--icon-style' height={20} />
                        <div>The account can be reactivated at any time from this screen. Data is never deleted.</div>
                    </div>
                </div>

                <div className="ar-modal__footer">
                    <button className="ar-btn ar-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="ar-btn ar-btn--deactivate-solid" onClick={handleSubmit} disabled={!selectedAdmin || submitting}>
                        {submitting ? 'Deactivating...' : 'Deactivate account'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

// ─── Create new role Modal ───────────────────────────────────────────────────────

function CreateRoleModal({ onClose, onRoleAdded }) {
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [roleObj, setRoleObj] = useState({ name: '', description: '', icon: '' })
    const [fieldError, setFieldError] = useState({})

    const handleSubmit = async () => {

        setSubmitting(true)
        setError('')

        try {
            const payload = {
                display_name: roleObj.name,
                description: roleObj.description,
                icon: roleObj.icon || null,
                permission_ids: [],
            }

            const response = await merchantApi.roles.create(payload)
            onRoleAdded(response, payload)
            onClose()
            setFieldError({})
            setError('')
        } catch (err) {
            setError(err?.message || 'Unable to create role.')
            setFieldError(err?.data?.errors || {})
        } finally {
            setSubmitting(false)
        }
    }

    return createPortal(
        <div className="ar-modal-overlay" onClick={onClose}>
            <div className="ar-modal" onClick={(e) => e.stopPropagation()}>

                <div className="ar-modal__header">
                    <h3>Add new role</h3>
                    <button className="ar-modal__close" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="ar-modal__body">
                    {error && <div className="ar-error">{error}</div>}

                    <Grid container spacing={2}>
                        <Grid item sm={12} xs={12}>
                            <div className="ar-modal__field">
                                <label>Role</label>
                                <input className="ar-input" type="text" placeholder="Role name" value={roleObj.name} onChange={(e) => setRoleObj({ ...roleObj, name: e.target.value })} />
                                {fieldError?.display_name && <div className="ar-error-message">{fieldError.display_name}</div>}
                            </div>
                        </Grid>
                        <Grid item sm={12} xs={12}>
                            <IconSelect
                                label="Icon"
                                value={roleObj.icon}
                                options={ICON_OPTIONS}
                                onChange={(e) => setRoleObj({ ...roleObj, icon: e.target.value })}
                                placeholder="Select icon"
                            />
                        </Grid>
                        <Grid item sm={12} xs={12}>
                            <div className="ar-modal__field">
                                <label>Description</label>
                                <input className="ar-input" type="text" placeholder="Role description" value={roleObj.description} onChange={(e) => setRoleObj({ ...roleObj, description: e.target.value })} />
                            </div>
                        </Grid>
                    </Grid>
                </div>

                <div className="ar-modal__footer">
                    <button className="ar-btn ar-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="ar-btn ar-btn--standard" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Creating...' : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

// ─── Admin Details Panel ──────────────────────────────────────────────────────

function AdminDetailsPanel({ admin, onClose, setSelectedDeactivateAdmin, onUpdateAdmin, editable, fieldError }) {
    const [name, setName] = useState(admin.name)
    const [phone, setPhone] = useState(admin.phone)
    const [phoneCountryCode, setPhoneCountryCode] = useState(admin.countryCode || '60')
    const [email, setEmail] = useState(admin?.email)
    const handleSaveAdmin = () => {
        const payload = {
            name: name.trim(),
            email: email?.trim() || null,
            role_id: admin.role_id,
            phone: phoneCountryCode + normalizePhoneNumber(phone),
        }
        onUpdateAdmin(admin.id, payload)
    }
    return (
        <>
            <div className="ar-panel-overlay" onClick={onClose} />
            <aside className="ar-panel">

                {/* Top bar */}
                <div className="ar-panel__header">
                    <h3>Admin details</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>

                {/* Hero */}
                <div className="ar-panel__hero">
                    <div className="ar-panel__hero-profile">
                        {
                            admin?.profile_photo_url ?
                                <img src={admin.profile_photo_url} alt={admin.name} className="ar-panel__avatar" />
                                : <Avatar className="ar-panel__mui-avatar" />
                        }

                        <div className="ar-panel__hero-info">
                            <p className="ar-panel__hero-name" title={admin.name}>{admin.name}</p>
                            <p className="ar-panel__hero-meta">
                                <span className="ar-panel__hero-email" title={admin.email}>
                                    {admin.email || '-'}
                                </span>
                                <span className="ar-panel__sep">·</span>
                                <span className="ar-panel__hero-joined">
                                    Joined {admin.joined_at ? moment(admin.joined_at).format('MMM YYYY') : 'N/A'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="ar-panel__hero-badges">
                        <RoleBadge roleId={admin.role_name} label={admin.role_display_name} />
                        <span className={`ar-status-badge ar-status-badge--${admin.status === 'invited' ? 'pending-invite' : admin.status}`}>
                            <Icon icon="ph:circle-fill" height={5} /> {admin.status ? admin.status[0].toUpperCase() + admin.status.slice(1) : '-'}
                        </span>
                    </div>
                </div>

                {/* Form body */}
                <div className="ar-panel__body">
                    <p className="ar-panel__section-label">BASIC INFO</p>

                    {/* Name */}
                    <div className="ar-panel__field">
                        <label>Name</label>
                        <input
                            className="ar-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={!editable}
                            placeholder="Amber Ng Zi Qing"
                        />
                        {fieldError?.name && <div className="ar-error-message">{fieldError.name}</div>}
                    </div>

                    {/* Role — read only */}
                    <div className="ar-panel__field">
                        <label>Role</label>
                        <input
                            className="ar-input ar-input--readonly"
                            type="text"
                            value={admin?.role_display_name}
                            readOnly
                        />
                        {/* <p className="ar-panel__field-hint">{role?.description}</p> */}
                    </div>

                    {/* Phone */}
                    <div className="ar-panel__field">
                        <label>Phone number</label>
                        <PhoneInputField
                            value={phone}
                            onChange={setPhone}
                            countryCode={phoneCountryCode}
                            onCountryCodeChange={setPhoneCountryCode}
                            disabled={!editable}
                            placeholder="123889021"
                        />
                        {fieldError?.phone && <div className="ar-error-message">{fieldError.phone}</div>}
                    </div>

                    {/* Email */}
                    <div className="ar-panel__field">
                        <label>
                            Email address
                            <span className="ar-panel__optional">(optional)</span>
                        </label>
                        <input
                            className="ar-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={!editable}
                            placeholder="example@app.com"
                        />
                        {fieldError?.email && <div className="ar-error-message">{fieldError.email}</div>}
                    </div>

                </div>

                {/* Footer — two equal buttons */}
                {
                    editable &&
                    <div className="ar-panel__footer">
                        <button className="ar-btn ar-btn--deactivate" onClick={() => setSelectedDeactivateAdmin(admin)}>Deactivate account</button>
                        <button className="ar-btn ar-btn--save" onClick={() => handleSaveAdmin()}>Save changes</button>
                    </div>
                }
            </aside>
        </>
    )
}

// ─── Roles & Permissions Tab ──────────────────────────────────────────────────

function buildInitialPerms({ roleList }) {
    const state = {}
    roleList.forEach((role) => {
        const granted = new Set()
        // Initialize from role.permissions (could be array or object)
        if (role.permissions) {
            if (Array.isArray(role.permissions)) {
                role.permissions.forEach((perm) => granted.add(perm.name || perm.key || perm))
            } else if (typeof role.permissions === 'object') {
                // If role.permissions is an object (module -> array of permissions)
                Object.values(role.permissions).forEach((modulePerms) => {
                    if (Array.isArray(modulePerms)) {
                        modulePerms.forEach((perm) => {
                            if (perm.enabled) {
                                granted.add(perm.key)
                            }
                        })
                    }
                })
            }
        } else if (role.access_set) {
            role.access_set.forEach((module) => {
                module.permissions.forEach((perm) => {
                    if (perm.enabled) {
                        granted.add(perm.name)
                    }
                })
            })
        }
        state[role.id] = granted
    })
    return state
}

// Helper function to get grouped permissions from a role's access_set or permissions object
const getGroupedPermissionsForRole = (role) => {
    if (role.permissions && typeof role.permissions === 'object' && !Array.isArray(role.permissions)) {
        // Use role.permissions object structure
        return Object.entries(role.permissions).reduce((groups, [moduleName, modulePerms]) => {
            if (Array.isArray(modulePerms)) {
                groups[moduleName] = modulePerms.map(p => ({
                    key: p.key,
                    label: p.label
                }))
            }
            return groups
        }, {})
    }
    if (!role.access_set) return {}
    return role.access_set.reduce((groups, module) => {
        groups[module.label] = module.permissions.map(p => ({
            key: p.name,
            label: p.label
        }))
        return groups
    }, {})
}

const groupPermissions = (permissions = []) => permissions.reduce((groups, permission) => {
    const moduleName = String(permission.name || 'other').split('.')[0]
    const label = moduleName
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase())

    if (!groups[label]) groups[label] = []
    groups[label].push({
        id: permission.id,
        key: permission.name,
        label: permission.display_name || permission.name,
    })

    return groups
}, {})

function RolesTab({ setShowAddRole, roleList, setRoleList, permissionDetails, onRoleSaved }) {
    const [allPerms, setAllPerms] = useState(buildInitialPerms({ roleList }))
    // All collapsed by default
    const [expanded, setExpanded] = useState({})
    const [loadingRoles, setLoadingRoles] = useState(new Set())
    const [savingRoles, setSavingRoles] = useState(new Set())
    const [saveError, setSaveError] = useState('')
    const [fieldError, setFieldError] = useState({})

    // Update allPerms when roleList changes
    useEffect(() => {
        setAllPerms(buildInitialPerms({ roleList }))
    }, [roleList])

    const fetchRoleDetails = async (roleId) => {
        if (loadingRoles.has(roleId)) return
        setLoadingRoles((prev) => new Set([...prev, roleId]))
        try {
            const response = await merchantApi.roles.show(roleId)
            const roleDetails = response?.data
            if (roleDetails) {
                setRoleList((prev) =>
                    prev.map((role) =>
                        role.id === roleId ? { ...role, ...roleDetails } : role
                    )
                )
            }
        } catch (err) {
            console.error("Error fetching role details:", err)
        } finally {
            setLoadingRoles((prev) => {
                const next = new Set(prev)
                next.delete(roleId)
                return next
            })
        }
    }

    const toggleExpand = (roleId) => {
        const isExpanding = !expanded[roleId]
        setExpanded((prev) => ({ ...prev, [roleId]: isExpanding }))
        if (isExpanding) {
            fetchRoleDetails(roleId)
        }
    }

    const togglePerm = (roleId, key) => {
        setAllPerms((prev) => {
            const next = new Set(prev[roleId])
            next.has(key) ? next.delete(key) : next.add(key)
            return { ...prev, [roleId]: next }
        })
    }

    const toggleAllPerms = (roleId, groupedPermissions) => {
        setAllPerms((prev) => {
            const next = new Set(prev[roleId])
            // Get all permission keys from groupedPermissions
            const allKeys = Object.values(groupedPermissions).flatMap(module =>
                module.map(permission => permission.key)
            )
            const hasAll = allKeys.every((key) => next.has(key))
            if (hasAll) {
                // Remove all
                allKeys.forEach((key) => next.delete(key))
            } else {
                // Add all
                allKeys.forEach((key) => next.add(key))
            }
            return { ...prev, [roleId]: next }
        })
    }

    const handleRoleInfoChange = (roleId, field, value) => {
        setRoleList((prev) => {
            const next = prev.map((role) => {
                if (role.id === roleId) {
                    return { ...role, [field]: value }
                }
                return role
            })
            return next
        })
    }

    const handleSaveRole = async (role, event) => {
        event?.preventDefault()
        event?.stopPropagation()

        const selectedKeys = Array.from(allPerms[role.id] || [])
        const permissionIdMap = new Map(
            permissionDetails.map((permission) => [permission.name, Number(permission.id)])
        )
        const permissionIds = Array.from(new Set(
            selectedKeys
                .map((key) => permissionIdMap.get(key))
                .filter((id) => Number.isFinite(id))
        ))

        if (selectedKeys.length > 0 && permissionIds.length === 0) {
            setSaveError('Unable to resolve permission IDs for this role. Please reload role details and try again.')
            return
        }

        const payload = {
            display_name: role.display_name,
            icon: role.icon,
            description: role.description,
            permission_ids: permissionIds,
        }

        setSaveError('')
        setSavingRoles((prev) => new Set([...prev, role.id]))
        try {
            await merchantApi.roles.update(role.id, payload)

            onRoleSaved?.(role, payload)
            setFieldError({})
            setSaveError('')
        } catch (err) {
            setSaveError(err?.message || 'Unable to update role.')
            setFieldError(err?.data?.errors || {})
        } finally {
            setExpanded((prev) => ({ ...prev, [role.id]: false }))
            setSavingRoles((prev) => {
                const next = new Set(prev)
                next.delete(role.id)
                return next
            })
        }
    }

    console.log("role list", roleList)


    return (
        <div className="ar-roles-list">
            <div className="ar-roles-header">
                <h1 className="ar-page__title">Roles & Permissions</h1>
                <button className="ar-btn ar-btn--primary" onClick={() => setShowAddRole(true)}>
                    Add Role
                </button>
            </div>
            {saveError && <div className="ar-error">{saveError}</div>}
            {roleList?.length > 0 ? roleList.map((role) => {
                const granted = allPerms[role.id] || new Set()
                const isOpen = !!expanded[role.id]
                const groupedPermissions = groupPermissions(permissionDetails)
                const allKeys = Object.values(groupedPermissions).flatMap(module =>
                    module.map(permission => permission.key)
                )
                console.log("groupedPermissions", groupedPermissions)
                const hasAll = allKeys.every((key) => granted?.has(key))
                return (
                    <div key={role.id} className={`ar-role-card ${isOpen ? 'ar-role-card--open' : ''}`}>

                        {/* Clickable header — chevron right = collapsed, down = expanded */}
                        <button
                            className="ar-role-card__header"
                            onClick={() => toggleExpand(role.id)}
                            aria-expanded={isOpen}
                            type="button"
                        >
                            <div className="ar-role-card__title-row">
                                <span className={`ar-role-card__icon`}>
                                    <Icon icon={role.icon || 'ph:users-fill'} size={16} />
                                </span>
                                <div>
                                    <p className="ar-role-card__name">{role.display_name}</p>
                                    <p className="ar-role-card__desc">{role.description}</p>
                                </div>
                            </div>

                            <div className="ar-role-card__header-right">
                                {isOpen ? (
                                    <button
                                        className="ar-btn ar-btn--primary"
                                        type="button"
                                        onClick={(event) => handleSaveRole(role, event)}
                                        disabled={loadingRoles.has(role.id) || savingRoles.has(role.id)}
                                    >
                                        {savingRoles.has(role.id) ? 'Saving...' : 'Save'}
                                    </button>
                                ) : 'Edit'}
                                <span className="ar-role-card__edit-label">
                                    {isOpen ? <Icon icon={'ph:caret-up'} size={16} /> : <Icon icon={'ph:caret-right'} size={16} />}
                                </span>
                            </div>
                        </button>

                        {/* Collapsible permissions body */}
                        {isOpen && (
                            <Grid container spacing={2} p={4}>
                                {loadingRoles.has(role.id) ? (
                                    <Grid item sm={12} xs={12}>
                                        <div className="ar-loading">Loading role permissions...</div>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item sm={12}>
                                            <p className="ar-perm-section__label">Role</p>
                                        </Grid>
                                        <Grid item sm={6} xs={12}>
                                            <div className="ar-modal__field">
                                                <label>Role</label>
                                                <input className="ar-input" type="text" placeholder="Role name" value={role.display_name} onChange={(e) => handleRoleInfoChange(role.id, 'display_name', e.target.value)} />
                                                {fieldError?.display_name && <div className="ar-error-message">{fieldError.display_name}</div>}
                                            </div>
                                        </Grid>
                                        <Grid item sm={6} xs={12}>
                                            <IconSelect
                                                label="Icon"
                                                value={role.icon}
                                                options={ICON_OPTIONS}
                                                onChange={(e) => handleRoleInfoChange(role.id, 'icon', e.target.value)}
                                                placeholder="Select icon"
                                            />
                                        </Grid>
                                        <Grid item sm={12} xs={12}>
                                            <div className="ar-modal__field">
                                                <label>Description</label>
                                                <input className="ar-input" type="text" placeholder="Role description" value={role.description} onChange={(e) => handleRoleInfoChange(role.id, 'description', e.target.value)} />
                                            </div>
                                        </Grid>
                                        <Grid item sm={12} xs={12} my={2}><Divider /></Grid>
                                        <Grid item sm={6} xs={12}>
                                            <p className="ar-perm-section__label">Permission Settings</p>
                                        </Grid>
                                        <Grid item sm={12} xs={12}>
                                            <label key={'select-all' + role.name} className="ar-perm-row">
                                                <span
                                                    className={`ar-perm-toggle ${hasAll ? 'ar-perm-toggle--on' : ''}`}
                                                    onClick={(e) => { e.preventDefault(); toggleAllPerms(role.id, groupedPermissions) }}
                                                    role="switch"
                                                    aria-checked={hasAll}
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === ' ' && toggleAllPerms(role.id, groupedPermissions)}
                                                >
                                                    <span className="ar-perm-toggle__thumb" />
                                                </span>
                                                <span className="ar-perm-row__name">Select all</span>
                                            </label>
                                        </Grid>
                                        {groupedPermissions && Object.entries(groupedPermissions).map(([moduleName, sections]) => (
                                            <Grid key={moduleName} item sm={6} xs={12}>
                                                <div className="ar-perm-section">
                                                    <p className="ar-perm-section__label">{moduleName}<Divider /></p>
                                                    {sections.map((section, idx) => {
                                                        const key = section.key
                                                        const on = granted.has(key)
                                                        return (
                                                            <label key={key} className="ar-perm-row">
                                                                <span
                                                                    className={`ar-perm-toggle ${on ? 'ar-perm-toggle--on' : ''}`}
                                                                    onClick={(e) => { e.preventDefault(); togglePerm(role.id, key) }}
                                                                    role="switch"
                                                                    aria-checked={on}
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => e.key === ' ' && togglePerm(role.id, key)}
                                                                >
                                                                    <span className="ar-perm-toggle__thumb" />
                                                                </span>
                                                                <span className="ar-perm-row__name">{section.label}</span>
                                                            </label>
                                                        )
                                                    })}
                                                </div>
                                            </Grid>
                                        ))}
                                    </>
                                )}
                            </Grid>
                        )}

                    </div>
                )
            }) : <div>No roles available</div>}
        </div>
    )
}

// ─── Admin Members Tab ────────────────────────────────────────────────────────

function AdminTab({ adminRows = null, loading = false, error = '', search, onSelect, onPageChange, onSearchChange, filter, setFilter, roleOptions, setSelectedDeactivateAdmin, onActivate }) {

    const columns = [
        {
            field: 'name',
            header: 'Name',
            render: (row) => (
                <div className="admin-info">
                    <span>{row.name}</span>
                    <div>{row.email}</div>
                </div>
            ),
        },
        {
            field: 'role_display_name',
            header: 'Role',
            render: (row) => <RoleBadge roleId={row.role_name} label={row.role_display_name} />,
        },
        {
            field: 'phone',
            header: 'Phone',
            render: (row) => `+${row.countryCode}${row.phone}`,
        },
        {
            field: 'status',
            header: 'Status',
            render: (row) => (
                <div className={`ar-status-badge ar-status-badge--${row.status === 'invited' ? 'pending-invite' : row.status}`}>
                    <Icon icon="ph:circle-fill" height={5} /> {row.status ? row.status[0].toUpperCase() + row.status.slice(1) : '-'}
                </div>
            ),
        },
        {
            field: 'last_logged_in',
            header: 'Last Login',
            render: (row) => row.last_logged_in ? moment(row.last_logged_in).format('DD/MM/YYYY') : '-',
        },
        {
            field: 'actions',
            header: '',
            render: (row) => {
                const menuOptions = [
                    { label: 'View details', onClick: () => onSelect(row), icon: 'ph:user' },
                    {
                        label: row.status === 'active' ? 'Deactivate' : 'Activate',
                        onClick: () => row.status === 'active'
                            ? setSelectedDeactivateAdmin(row)
                            : onActivate(row),
                        icon: row.status === 'active' ? 'ph:prohibit' : 'ph:check',
                        color: row.status === 'active' ? 'red' : 'default',
                    },
                ]
                return (
                    <>
                        {
                            row.status !== 'invited' &&
                            <AppMenu
                                options={menuOptions}
                            />
                        }
                    </>
                )
            }
        }
    ]

    return (
        <>
            {error && <div className="ar-error">{error}</div>}
            {loading && <div className="ar-loading">Loading admin...</div>}

            <AppTable
                columns={columns}
                rows={adminRows?.data || []}
                pagination={adminRows}
                loading={loading}
                onRowClick={(row) => { if (row.status !== 'invited') onSelect(row) }}
                onPageChange={(page) => {
                    onPageChange(page)
                }}
                toolBar={
                    <Box className="ar-table-toolbar">
                        <Box className="flex-container">
                            <FilterDropdown
                                label="Status"
                                value={filter?.status}
                                onChange={(value) => setFilter({ ...filter, status: value })}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'invited', label: 'Pending invite' },
                                    { value: 'deactivated', label: 'Inactive' },
                                ]}
                            />
                            <FilterDropdown
                                label="roles"
                                value={filter?.role}
                                onChange={(value) => setFilter({ ...filter, role: value })}
                                options={roleOptions}
                                valueKey="value"
                                labelKey="label"
                            />
                            <button type="button" className="pos-toolbar__clear" onClick={() => setFilter({})}>Clear all</button>
                        </Box>
                        <Box className="toolbar-search">
                            <Icon icon="ph:magnifying-glass" height={18} />
                            <input type="text" placeholder="Search users" value={search} onChange={(e) => onSearchChange(e)} />
                        </Box>
                    </Box>
                }
            />
        </>
    )
}

function AdminFilter({ open, setOpen, setValues, values, applyFilters, roleOptions }) {
    const filters = [
        {
            key: 'role',
            label: 'Role',
            type: 'chip',
            options: roleOptions,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'chip',
            options: [
                { value: 'active', label: 'Active' },
                { value: 'invited', label: 'Pending invite' },
                { value: 'deactivated', label: 'Inactive' },
            ],
        },
    ]

    return (
        <FilterSideModal
            open={open}
            onClose={() => setOpen(false)}
            onApply={(vals) => {
                setOpen(false)
                applyFilters(vals)
            }}
            onReset={() => setValues({})}
            values={values}
            onChange={(key, val) => setValues((p) => ({ ...p, [key]: val }))}
            filters={filters}
        />
    )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function UserManagement() {
    const [activeTab, setActiveTab] = useState('admin')
    const [showInvite, setShowInvite] = useState(false)
    const [selectedAdmin, setSelectedAdmin] = useState(null)
    const [selectedDeactivateAdmin, setSelectedDeactivateAdmin] = useState(null)
    const [notice, setNotice] = useState('')
    const [pageError, setPageError] = useState('')
    const [fieldError, setFieldError] = useState('')
    const [adminLoading, setAdminLoading] = useState(false)
    const [showAddRole, setShowAddRole] = useState(false)
    const [adminList, setAdminList] = useState([])
    const [roleList, setRoleList] = useState([])
    const [roleOptions, setRoleOptions] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [search, setSearch] = useState('')
    const [permissionDetails, setPermissionDetails] = useState([])
    const [filter, setFilter] = useState({ outlet: '', status: '', role: '' })
    const [filterOpen, setFilterOpen] = useState(false)
    // permission states
    const [editable, setEditable] = useState(true)
    const [creatable, setCreatable] = useState(true)

    useEffect(() => {
        setFieldError({})
        setPageError('')
        setNotice('')

        loadRoleData()
        loadPermissionData()
        loadGeneralData()
    }, [])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadAdminData()
        }, search ? 350 : 0)

        return () => window.clearTimeout(timeoutId)
    }, [currentPage, search, filter.status, filter.role])

    const loadAdminData = async () => {
        setAdminLoading(true)
        setPageError('')

        try {
            const payload = {
                page: currentPage,
                search,
                role_id: filter?.role || undefined,
                status: filter?.status || undefined,
            }
            const response = await merchantApi.adminUsers.list(payload)
            const normalizedRows = (Array.isArray(response?.data) ? response.data : [])
                .map(normalizeAdmin)

            setAdminList({
                ...(response?.pagination || {}),
                data: normalizedRows,
            })
        } catch (error) {
            setPageError(error.message || 'Error fetching data.')
            setAdminList({ data: [] })
        } finally {
            setAdminLoading(false)
        }
    }

    const loadRoleData = async () => {
        try {
            const response = await merchantApi.roles.list()
            const roles = Array.isArray(response?.data) ? response.data : []
            setRoleList(roles)
        } catch (error) {
            setPageError(error.message || 'Error fetching data.')
        }
    }

    const loadPermissionData = async () => {
        try {
            const response = await merchantApi.permissions.list({ per_page: 100 })
            setPermissionDetails(Array.isArray(response?.data) ? response.data : [])
        } catch (error) {
            setPageError(error.message || 'Error fetching permissions.')
        }
    }

    const loadGeneralData = async () => {
        try {
            const response = await merchantApi.general.get()
            const roles = Array.isArray(response?.data?.roles) ? response.data.roles : []

            setRoleOptions(roles)
        } catch (error) {
            setPageError(error.message || 'Error fetching general data.')
        }
    }

    const adminRows = useMemo(() => adminList, [adminList])
    console.log('adminRows', adminRows)
    const handleInvited = (_response, payload) => {
        setNotice(`Invitation sent to ${payload.name}.`)
        loadAdminData()
    }

    const handleRoleAdded = (_response, payload) => {
        setNotice(`Role added: ${payload.display_name}.`)
        loadRoleData()
    }

    const handleDeactivated = (_response, payload) => {
        setNotice(`Account deactivated for ${payload.name}.`)
        setSelectedAdmin(null)
        loadAdminData()
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrentPage(1) // Reset to first page on new search
    }

    const handlePageChange = (page) => {
        console.log('handlePageChange parent', page)
        setCurrentPage(page)
    }

    const handleUpdateAdmin = async (adminId, payload) => {
        try {
            await merchantApi.adminUsers.update(adminId, payload)
            setNotice(`Admin member updated: ${payload.name}.`)
            setPageError('') // Clear any previous errors
            setFieldError({}) // Clear field errors
            setSelectedAdmin(null) // Close the details panel
            loadAdminData() // Refresh the admin list after update
        } catch (error) {
            setFieldError(error?.data?.errors || {})
            setPageError(error.message || 'Error updating admin member.')
        }
    }

    const handleSelectAdmin = async (admin) => {
        setPageError('')

        try {
            const response = await merchantApi.adminUsers.show(admin.id)
            setSelectedAdmin(normalizeAdmin(response?.data || admin))
        } catch (error) {
            setPageError(error.message || 'Error loading admin details.')
        }
    }

    const handleActivateAdmin = async (admin) => {
        try {
            await merchantApi.adminUsers.activate(admin.id)
            setNotice(`Account activated for ${admin.name}.`)
            await loadAdminData()
        } catch (error) {
            setPageError(error.message || 'Error activating admin account.')
        }
    }

    return (
        <div className="ar-page">
            {/* Header */}
            <Header
                items={[
                    { label: 'Cuscapi', to: '/' },
                    { label: 'User Management', current: true },
                ]}
            />
            <div className="ar-page__header">
                <div>
                    <h1 className="ar-page__title">User Management</h1>
                    <p className="ar-page__subtitle">Manage admin accounts — access control, roles, and audit log</p>
                </div>
                <div className="ar-page__actions">
                    {
                        activeTab === 'admin' && (
                            <button className="ar-btn ar-btn--outline" onClick={() => setFilterOpen(true)}>
                                <Icon icon="ph:funnel-simple" height={16} /> Filter
                            </button>
                        )
                    }
                    {
                        activeTab === 'admin' && creatable && (
                            <button className="ar-btn ar-btn--primary" onClick={() => setShowInvite(true)}>
                                <Icon icon={"ph:plus"} height={16} /> Invite admin user
                            </button>
                        )
                    }
                </div>
            </div>

            {notice && <div className="ar-success">{notice}</div>}
            {pageError && <div className="ar-error">{pageError}</div>}

            {/* Tabs */}
            <div className="ar-tabs">
                <button
                    className={`ar-tab ${activeTab === 'admin' ? 'ar-tab--active' : ''}`}
                    onClick={() => setActiveTab('admin')}
                >
                    Admin members
                </button>
                <button
                    className={`ar-tab ${activeTab === 'roles' ? 'ar-tab--active' : ''}`}
                    onClick={() => setActiveTab('roles')}
                >
                    Roles &amp; permissions
                </button>
            </div>

            {/* Tab content */}
            {
                activeTab === 'admin' && (
                    <AdminTab
                        adminRows={adminRows}
                        loading={adminLoading}
                        search={search}
                        setSelectedAdmin={setSelectedAdmin}
                        setSelectedDeactivateAdmin={setSelectedDeactivateAdmin}
                        onSelect={handleSelectAdmin}
                        onSearchChange={handleSearchChange}
                        setFilterOpen={setFilterOpen}
                        onPageChange={handlePageChange}
                        onActivate={handleActivateAdmin}
                        filter={filter}
                        setFilter={(nextFilter) => {
                            setFilter(nextFilter)
                            setCurrentPage(1)
                        }}
                        roleOptions={roleOptions}
                    />
                )
            }
            {
                activeTab === 'roles' &&
                <RolesTab
                    setShowAddRole={setShowAddRole}
                    roleList={roleList}
                    setRoleList={setRoleList}
                    permissionDetails={permissionDetails}
                    onRoleSaved={(role, payload) => {
                        setNotice(`Role updated: ${payload.display_name || role.display_name}.`)
                        loadRoleData()
                    }}
                />
            }

            {/* Panels & Modals */}
            {
                showInvite && (
                    <InviteModal
                        roles={roleList}
                        onClose={() => setShowInvite(false)}
                        onInvited={handleInvited}
                    />
                )
            }
            {
                showAddRole && (
                    <CreateRoleModal
                        onClose={() => setShowAddRole(false)}
                        onRoleAdded={handleRoleAdded}
                    />
                )
            }
            {
                selectedDeactivateAdmin && (
                    <DeactivateModal
                        selectedAdmin={selectedDeactivateAdmin}
                        onClose={() => setSelectedDeactivateAdmin(null)}
                        onDeactivated={handleDeactivated}
                    />
                )
            }
            {
                selectedAdmin && (
                    <AdminDetailsPanel
                        admin={selectedAdmin}
                        onClose={() => setSelectedAdmin(null)}
                        setSelectedDeactivateAdmin={setSelectedDeactivateAdmin}
                        onUpdateAdmin={handleUpdateAdmin}
                        editable={editable}
                        fieldError={fieldError}
                    />
                )
            }
            <AdminFilter
                open={filterOpen}
                setOpen={setFilterOpen}
                values={filter}
                setValues={(value) => {
                    setFilter(value)
                    setCurrentPage(1)
                }}
                applyFilters={loadAdminData}
                roleOptions={roleOptions}
            />
        </div >
    )
}
